import React, { useEffect, useState } from 'react';
import { AdminLayout } from '@/components/AdminLayout';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Plus, Trash2, ToggleLeft, ToggleRight, Pencil } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminRiders() {
  const [riders, setRiders] = useState([]);
  const [create, setCreate] = useState(null);
  const [edit, setEdit] = useState(null);

  const load = () => api.get('/admin/riders').then(r => setRiders(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post('/admin/riders', create);
      toast.success('Rider creato');
      setCreate(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore'); }
  };
  const saveEdit = async () => {
    if (!edit) return;
    const payload = {};
    if (edit.name !== edit._orig.name) payload.name = edit.name;
    if (edit.email !== edit._orig.email) payload.email = edit.email;
    if (edit.password) payload.password = edit.password;
    if (Object.keys(payload).length === 0) { toast.info('Nessuna modifica'); return; }
    try {
      await api.patch(`/admin/riders/${edit.id}`, payload);
      toast.success('Rider aggiornato');
      setEdit(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Errore aggiornamento'); }
  };
  const toggle = async (id) => { await api.patch(`/admin/riders/${id}/toggle`); load(); };
  const del = async (id) => { if (!window.confirm('Eliminare rider?')) return; await api.delete(`/admin/riders/${id}`); load(); };

  return (
    <AdminLayout testid="admin-riders-page">
      <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-[--brand-muted]">Riders</div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold">Gestione riders</h1>
        </div>
        <Button className="rounded-xl bg-[--brand-primary]" onClick={() => setCreate({ email: '', name: '', phone: '', password: '' })} data-testid="admin-rider-create-button">
          <Plus size={14} className="mr-1"/>Nuovo rider
        </Button>
      </div>

      <div className="rounded-2xl bg-white border border-[--brand-border] overflow-hidden" data-testid="admin-riders-table">
        <table className="w-full text-sm">
          <thead className="bg-[--brand-surface-2] text-left">
            <tr>
              <th className="p-3">Nome</th><th className="p-3">Email</th><th className="p-3">Telefono</th><th className="p-3">Stato</th><th className="p-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {riders.map(r => (
              <tr key={r.id} className="border-t border-[--brand-border]">
                <td className="p-3 font-semibold">{r.name}</td>
                <td className="p-3 text-[--brand-muted]">{r.email}</td>
                <td className="p-3 text-[--brand-muted]">{r.phone || '—'}</td>
                <td className="p-3"><Badge className={r.active ? 'bg-[#EAF6F1] text-[#1F6F5B]' : 'bg-[--brand-surface-2] text-[--brand-muted]'}>{r.active ? 'Attivo' : 'Disattivo'}</Badge></td>
                <td className="p-3">
                  <div className="flex justify-end gap-1">
                    <Button size="sm" variant="ghost" onClick={() => setEdit({ ...r, password: '', _orig: { name: r.name, email: r.email } })} data-testid="admin-rider-edit-button">
                      <Pencil size={16} className="text-[--brand-primary]"/>
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => toggle(r.id)} data-testid="admin-rider-status-toggle">
                      {r.active ? <ToggleRight size={16}/> : <ToggleLeft size={16}/>}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => del(r.id)}><Trash2 size={16} className="text-[--brand-danger]"/></Button>
                  </div>
                </td>
              </tr>
            ))}
            {riders.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-[--brand-muted]">Nessun rider ancora.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!create} onOpenChange={(o) => !o && setCreate(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Nuovo rider</DialogTitle></DialogHeader>
          {create && (
            <div className="space-y-3">
              <div><Label>Nome</Label><Input value={create.name} onChange={e => setCreate({...create, name: e.target.value})} className="rounded-xl"/></div>
              <div><Label>Email</Label><Input type="email" value={create.email} onChange={e => setCreate({...create, email: e.target.value})} className="rounded-xl"/></div>
              <div><Label>Telefono</Label><Input value={create.phone} onChange={e => setCreate({...create, phone: e.target.value})} className="rounded-xl"/></div>
              <div><Label>Password (min 6)</Label><Input type="text" value={create.password} onChange={e => setCreate({...create, password: e.target.value})} className="rounded-xl"/></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setCreate(null)}>Annulla</Button>
            <Button onClick={save} className="bg-[--brand-primary]">Crea</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle>Modifica rider</DialogTitle></DialogHeader>
          {edit && (
            <div className="space-y-3">
              <div><Label>Nome</Label><Input value={edit.name || ''} onChange={e => setEdit({...edit, name: e.target.value})} className="rounded-xl" data-testid="rider-edit-name"/></div>
              <div><Label>Email</Label><Input type="email" value={edit.email || ''} onChange={e => setEdit({...edit, email: e.target.value})} className="rounded-xl" data-testid="rider-edit-email"/></div>
              <div>
                <Label>Nuova password <span className="text-[--brand-muted] font-normal">(lascia vuoto per non cambiare)</span></Label>
                <Input type="text" value={edit.password} onChange={e => setEdit({...edit, password: e.target.value})} placeholder="Almeno 6 caratteri" className="rounded-xl" data-testid="rider-edit-password"/>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="secondary" onClick={() => setEdit(null)}>Annulla</Button>
            <Button onClick={saveEdit} className="bg-[--brand-primary]" data-testid="rider-edit-save">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
